import { isPlatformBrowser } from '@angular/common';
import { afterNextRender, computed, Injectable, inject, PLATFORM_ID, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CountryContext } from '../country/country-context.service';
import {
  CONSENT_STORAGE_KEY,
  CONSENT_VERSION,
  ConsentChoice,
  ConsentRecord,
  isConsentRecord,
} from './consent.model';

/**
 * Single source of truth for cookie consent on the markets that run our own
 * banner. Read it before doing anything that reports behaviour.
 *
 * Two signals, and picking the wrong one is the easy mistake here:
 *
 * - `analyticsAllowed` is what you gate a feature on. It is true on any
 *   market whose consent a hosted CMP governs (none today).
 * - `analyticsGranted` is this banner's own record, and is false on a
 *   hosted-CMP market because no record exists there. Gating such a market
 *   on it would switch its analytics off. Use it only to drive our own loader.
 *
 * `analyticsGranted$` is the same value as an observable for non-signal callers.
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
  private readonly platformId = inject<Object>(PLATFORM_ID);
  private readonly countryContext = inject(CountryContext);

  readonly selfHosted = this.countryContext.config.consentMode === 'self-hosted';

  private readonly record = signal<ConsentRecord | null>(null);
  /**
   * False until after hydration. The banner is a client-only decision (it needs
   * localStorage), so gating on this keeps the server render and the client's
   * first pass identical and avoids a hydration mismatch on the container.
   */
  private readonly ready = signal(false);
  private readonly reopened = signal(false);

  readonly decided = computed(() => this.record() !== null);
  readonly analyticsGranted = computed(() => this.record()?.categories.analytics === true);
  readonly analyticsGranted$ = toObservable(this.analyticsGranted);
  /**
   * The visitor's marketing choice. Nothing in the app gates on it directly:
   * the ad tags live inside the GTM container and read it through consent mode,
   * which AnalyticsLoaderService feeds from this signal.
   */
  readonly marketingGranted = computed(() => this.record()?.categories.marketing === true);

  /**
   * Whether an analytics-purpose feature may run on this market. This is the
   * signal to gate existing analytics on, not `analyticsGranted`.
   *
   * On a hosted-CMP market (consentMode 'cookieyes', none today) it is always
   * true: consent there is governed by the CMP and its own tag blocking, this
   * service holds no record, and gating on `analyticsGranted` would silently
   * switch that market's analytics off. On self-hosted markets it is the
   * visitor's actual choice, false until they make one.
   */
  readonly analyticsAllowed = computed(() => !this.selfHosted || this.analyticsGranted());

  /** The stored choice, for callers that need the timestamp or want to show it. */
  readonly currentRecord = this.record.asReadonly();

  readonly bannerOpen = computed(
    () => this.selfHosted && this.ready() && (!this.decided() || this.reopened())
  );

  constructor() {
    // Read synchronously on construction, not in afterNextRender. Callers like
    // the affiliate redirect decide within the same tick they are created in and
    // must not see "no consent" merely because a render pass has not run yet.
    // On the server there is no storage and nothing analytics-related runs.
    if (this.selfHosted && isPlatformBrowser(this.platformId)) {
      this.record.set(this.read());
    }

    // Only the banner waits for hydration. Keeping this separate from the read
    // above means consent is known immediately while the banner still stays out
    // of the server render and the client's first pass.
    afterNextRender(() => this.ready.set(true));
  }

  acceptAll(): void {
    this.write({ analytics: true, marketing: true });
  }

  rejectAll(): void {
    this.write({ analytics: false, marketing: false });
  }

  /** Saves the granular selection from the settings layer. */
  save(choice: ConsentChoice): void {
    this.write(choice);
  }

  /** Reopens the banner from the footer link so consent can be withdrawn. */
  reopen(): void {
    this.reopened.set(true);
  }

  private write(choice: ConsentChoice): void {
    const record: ConsentRecord = {
      version: CONSENT_VERSION,
      decidedAt: new Date().toISOString(),
      categories: { necessary: true, ...choice },
    };

    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
      } catch {
        // Private-mode or quota failure. The choice still applies to this page
        // view; the banner simply asks again next time rather than assuming
        // consent it could not record.
      }
    }

    this.reopened.set(false);
    this.record.set(record);
  }

  private read(): ConsentRecord | null {
    if (!isPlatformBrowser(this.platformId)) return null;

    let raw: string | null;
    try {
      raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    } catch {
      return null;
    }
    if (!raw) return null;

    try {
      const parsed: unknown = JSON.parse(raw);
      // A record from an older version, or one we cannot parse, is not consent.
      return isConsentRecord(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
}
